import { useEffect } from 'react';
import type { FieldErrors } from 'react-hook-form';

import { useScrollTo } from 'hooks/use-scroll-to';

import { hasNestedProperty } from '@login/helpers';
import type { IdmApiFieldErrors } from '@login/helpers/idm-errors';

const DEFAULT_OFFSET = 120;
const ALERT_SELECTOR = '[role="alert"]';

type UseScrollToFirstErrorOptions = {
	/** react-hook-form client-side errors. */
	errors: FieldErrors;
	/** API field errors, keyed by the PascalCase IDM field name. */
	apiFieldErrors?: IdmApiFieldErrors;
	/** Whether a page-level error banner is currently shown. */
	hasBanner?: boolean;
	/**
	 * Field paths in the order they appear on screen. react-hook-form does not
	 * guarantee key order on `errors`, so visual order has to be declared.
	 */
	fieldOrder: readonly string[];
	offset?: number;
};

const findFirstErrorElement = (
	fieldOrder: readonly string[],
	errors: FieldErrors,
	apiFieldErrors: IdmApiFieldErrors
): Element | null => {
	for (const path of fieldOrder) {
		if (hasNestedProperty(errors, path)) {
			return document.getElementById(`root-${path}`);
		}
		if (apiFieldErrors[path]?.length) {
			return document.querySelector(ALERT_SELECTOR);
		}
	}
	return null;
};

/**
 * Scrolls to the first field in error after a failed validation or submit.
 *
 * Replaces three near-identical effects in the register-style forms, each of
 * which carried its own hardcoded key list and its own nested-vs-flat lookup.
 */
export const useScrollToFirstError = ({
	errors,
	apiFieldErrors = {},
	hasBanner = false,
	fieldOrder,
	offset = DEFAULT_OFFSET,
}: UseScrollToFirstErrorOptions) => {
	const { scrollToSection } = useScrollTo();

	useEffect(() => {
		const target = hasBanner
			? document.querySelector(ALERT_SELECTOR)
			: findFirstErrorElement(fieldOrder, errors, apiFieldErrors);

		if (target) scrollToSection({ sectionRef: target, offset });
	}, [
		errors,
		apiFieldErrors,
		hasBanner,
		fieldOrder,
		offset,
		scrollToSection,
	]);
};

export default useScrollToFirstError;
